import dayjs from 'dayjs';

import {
  Tooltip,
  Typography,
  Box,
  Stack,
  colorOrToken,
  tokens,
} from '@cast/design-system';
import { K8sProvider } from '@cast/shared-types';
import {
  Cluster,
  ClusterReconcileStatus,
  type ClusterReconcileInfo as ClusterReconcileInfoData,
} from '@cast/types';

const DATE_FORMAT = 'MMMM D, HH:mm';

const getReconciledAt = (reconcileInfo?: ClusterReconcileInfoData) => {
  if (!reconcileInfo) {
    return (
      <Typography
        variant="L10M"
        color={colorOrToken('grey.500', tokens.theme.fg.structural.secondary)}
      >
        -
      </Typography>
    );
  }

  const { status, reconciledAt } = reconcileInfo;

  if (!reconciledAt) {
    return (
      <Typography
        variant="L10M"
        color={colorOrToken('grey.500', tokens.theme.fg.structural.secondary)}
      >
        IN PROGRESS...
      </Typography>
    );
  }

  return (
    <Typography
      variant="L10M"
      color={colorOrToken('grey.500', tokens.theme.fg.structural.secondary)}
    >
      {dayjs(reconciledAt).format(DATE_FORMAT)}
      {status === ClusterReconcileStatus.failed && (
        <Box
          component="span"
          color={colorOrToken('red.500', tokens.theme.fg.status.error)}
        >
          {' - FAILED'}
        </Box>
      )}
    </Typography>
  );
};

type ClusterReconcileInfoProps = {
  cluster: Cluster;
};

export const ClusterReconcileInfo = ({
  cluster,
}: ClusterReconcileInfoProps) => {
  const { agentSnapshotReceivedAt, reconcileInfo, providerType } = cluster;

  return (
    <Stack>
      <Tooltip
        title="The cluster snapshot is received every 15-30 seconds"
        arrow
        placement="top"
      >
        <Stack direction="row">
          <Typography
            variant="L10M"
            color={colorOrToken(
              'grey.500',
              tokens.theme.fg.structural.secondary
            )}
            sx={{ width: 65 }}
          >
            SNAPSHOT:
          </Typography>
          <Typography
            variant="L10M"
            color={colorOrToken(
              'grey.500',
              tokens.theme.fg.structural.secondary
            )}
          >
            {dayjs(agentSnapshotReceivedAt).format(DATE_FORMAT)}
          </Typography>
        </Stack>
      </Tooltip>
      {providerType !== K8sProvider.ANYWHERE ? (
        <Tooltip
          title="The cluster is reconciled every 30 minutes"
          arrow
          placement="bottom"
        >
          <Stack direction="row">
            <Typography
              variant="L10M"
              color={colorOrToken(
                'grey.500',
                tokens.theme.fg.structural.secondary
              )}
              sx={{ width: 65 }}
            >
              RECONCILE:
            </Typography>
            {getReconciledAt(reconcileInfo)}
          </Stack>
        </Tooltip>
      ) : null}
    </Stack>
  );
};
